<template>
    <div class="w-100 h-100">
        <b-modal
            v-model="showModal"
            hide-header
            hide-header-close
            centered
            no-close-on-backdrop
            :ok-only="ModalMessage == 'Submitted successfully'"
            @ok="clearFields"
        >
            <div class="text-center">
                <div>{{ModalMessage}}</div>
            </div>
        </b-modal>
        <b-container fluid>
            <h2 class="tw-mb-0 text-aegis-dk">Product Bag Preparation</h2>
            <p class="tw-italic tw-text-gray-700">
            Prepare bag for product replacement
            </p>
        </b-container>
        <br />
        <!-- 1. When the page first loads, allow the user to find the CaseProductEnrolledStructure -->
        <!-- 2. Once found, figure out how many products are required. -->
        <b-container fluid v-if="household_id == ''">
            <b-row>
                <b-col cols="3"></b-col>
                <b-col cols="4">
                    <!-- Example Household ID: K-22-384, cluster_number: 22 so barcodes need to start with 88 -->
                    <!--                       N-08-166, cluster_number: 08 so barcodes need to start with 79 -->
                    <b-form-input
                        ref="searchInput"
                        v-model="search_term"
                        placeholder="Enter Household ID"
                        @keypress.enter="getProductEntrolledStructure"
                    ></b-form-input>
                    <!-- :disabled="household_id != ''" -->
                </b-col>
                <b-col cols="2">
                    <b-button
                        @click="getProductEntrolledStructure"
                        :disabled="household_id != ''"
                    >Search</b-button>
                    <b-button class="ml-2" @click="clearFields()" variant="danger">
                       Clear
                    </b-button>
                </b-col>
                <b-col cols="3"></b-col>
            </b-row>
        </b-container>
        <!-- 3. Scan the bag barcode -->
        <!-- 4. Show a number of barcode inputs equal to the required number found in step 1 -->
        <b-container fluid v-else>
            <center style="color: red;" v-if="placement_status == 'prior'">
                <h4>This household has received product prior to portal functionality.</h4>
            </center>
        <b-card bg-variant="light" class="tw-mx-3">
            <b-row>
                <b-col cols="1"></b-col>
                <b-col cols="10">
                    <h4>Preparing product bag for household ID: {{household_id}}</h4>
                    <p>This structure requires {{products_required}} products.</p>
                    <br />
                </b-col>
                <b-col cols="1"></b-col>
            </b-row>
            <b-row>
                <b-col cols="2"></b-col>
                <b-col cols="8">
                    <h5>Placement type:</h5>
                </b-col>
                <b-col cols="2"></b-col>
            </b-row>
            <b-row>
                <b-col cols="1"></b-col>
                <b-col cols="10" align="center">
                    <div>
                        <b-form-radio-group
                            v-model="placement_opt_selected"
                            :options="filteredOptions"
                            inline
                            size="lg"
                        ></b-form-radio-group>
                    </div>
                </b-col>
                <b-col cols="1"></b-col>
            </b-row>
            <span  v-if="placement_opt_selected != 'final'">
                <b-row>
                    <b-col cols="2"></b-col>
                    <b-col cols="8">
                        <h5>Scan new products bag barcode:</h5>
                    </b-col>
                    <b-col cols="2"></b-col>
                </b-row>
                <b-row>
                    <b-col cols="3"></b-col>
                    <b-col cols="6">
                        <template v-if="isNewAndUsedBagBarcodesSame()">
                            <span class="text-danger">The New Product Bag and Used Product Bag barcodes cannot be the same.<br></span>
                        </template>
                        <template v-if="doesNotContainHouseholdID('new')">
                            <span class="text-danger">The barcode must contain the household ID: {{ household_id }}.<br></span>
                        </template>
                        <b-form-input
                            ref="NPBBInput"
                            v-model="new_product_bag_barcode"
                            placeholder="Scan product bag barcode"
                            @keydown.enter.prevent="advanceInput(0)"
                        ></b-form-input>
                        <br />
                    </b-col>
                    <b-col cols="3"></b-col>
                </b-row>
                <b-row>
                    <b-col cols="2"></b-col>
                    <b-col cols="8">
                        <h5>Scan each product and place it in the bag:</h5>
                    </b-col>
                    <b-col cols="2"></b-col>
                </b-row>
                <b-row>
                    <b-col cols="3"></b-col>
                    <b-col cols="6">
                        <span v-for="(field, index) in product_barcode_fields" :key="field.key" ref="rows">
                            <b-row class="mt-2">
                                <b-col>
                                    <b-form-group
                                        label-cols-sm="3"
                                        :id=field.key
                                        :label=field.text
                                    >
                                    <template v-if="isDuplicate(field)">
                                        <span class="text-danger">Duplicate barcode entered.<br></span>
                                    </template>
                                    <template v-if="isNotCorrectLength(field)">
                                        <span class="text-danger">The barcode must be 9 characters in length.<br></span>
                                    </template>
                                    <template v-if="isNotCorrectPrefix(field)">
                                        <span class="text-danger">The barcode must start with {{cluster_barcode_map[cluster_number]}} for cluster {{ cluster_number }}.<br></span>
                                    </template>
                                        <b-form-input
                                            :id="'PBInput' + (index + 1)"
                                            v-model="field.value"
                                            :placeholder=field.text
                                            @keydown.enter.prevent="advanceInput(index + 1)"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </span>
                    </b-col>
                </b-row>
            </span>
            <span  v-if="placement_opt_selected != 'initial'">
                <b-row>
                    <b-col cols="2"></b-col>
                    <b-col cols="8">
                        <h5>Scan used products bag barcode:</h5>
                    </b-col>
                    <b-col cols="2"></b-col>
                </b-row>
                <b-row v-if="placement_opt_selected != 'initial'">
                    <b-col cols="3"></b-col>
                    <b-col cols="6">
                        <template v-if="isNewAndUsedBagBarcodesSame()">
                            <span class="text-danger">The New Product Bag and Used Product Bag barcodes cannot be the same.<br></span>
                        </template>
                        <template v-if="doesNotContainHouseholdID('used')">
                            <span class="text-danger">The barcode must contain the household ID: {{ household_id }}.<br></span>
                        </template>
                        <b-form-input
                            ref="UPBBInput"
                            v-model="used_product_bag_barcode"
                            placeholder="Scan product bag barcode"
                            @keydown.enter.prevent="advanceInput(999)"
                        ></b-form-input>
                        <br />
                    </b-col>
                    <b-col cols="3"></b-col>
                </b-row>
            </span>
            <b-row>
                <b-col cols="3"></b-col>
                <b-col>
            <!-- 5. Check? -->
            <!-- 6. Submit or cancel -->
                    <b-button
                        ref="SubmitButton"
                        class="m-2 col-5"
                        variant="success"
                        @click="submitProductBag('update')"
                        :disabled="processing_submit_disabled"
                    >Submit </b-button>
                    <b-button
                        class="m-2 col-5"
                        variant="danger"
                        @click="clearFields()"
                    >Clear</b-button>
                </b-col>
                <b-col cols="3"></b-col>
            </b-row>
        </b-card>
        </b-container>

    </div>
  </template>
  
<script>
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";
import * as _ from "lodash";
  
import DashboardBaseTemplate from "@/layouts/DashboardBaseTemplate.vue";
  
export default {
mixins: [
    DashboardBaseTemplate,
],
components: {},
props: {},
watch: {
    household_id: function() {
        if (this.placement_opt_selected != 'final') {
            // sleep for .25 seconds to allow the DOM to update
            setTimeout(() => {
                this.$refs.NPBBInput.focus();
            }, 250);
        } else {
            setTimeout(() => {
                this.$refs.UPBBInput.focus();
            }, 250);
        }
    }

},
data: function() {
    return {
        placement_opt_selected:'replacement',
        placement_options: [
            { text: 'Initial', value: 'initial' },
            { text: 'Replacement', value: 'replacement' },
            { text: 'Final', value: 'final' },
        ],
        showModal: false,
        ModalMessage: '',
        search_term: '',
        new_product_bag_barcode: '',
        used_product_bag_barcode: '',
        cpes_id: '',
        household_id: '',
        cluster_number: '',
        products_required: 0,
        product_barcode_fields: [],
        cluster_barcode_map: {
            // # Negombo
            '1' : '81',
            '2' : '92',
            '3' : '73',
            '4' : '84',
            '5' : '80',
            '6' : '62',
            '7' : '63',
            '8' : '79',
            '9' : '94',
            '10' : '74',
            // # Wattala
            '11' : '66',
            '12' : '85',
            '13' : '77',
            '14' : '78',
            '15' : '69',
            '16' : '76',
            '17' : '72',
            '18' : '86',
            '19' : '87',
            '20' : '71',
            // # Kelaniya
            '21' : '70',
            '22' : '88',
            '23' : '83',
            '24' : '65',
            '25' : '82',
            '26' : '61',
            '27' : '67',
            '28' : '68',
            '29' : '95',
            '30' : '75',
        },
        placement_status: '',
        submit_ready: true  // If you hit the "Submit" button rapidly, it would let you submit multiple times. This is to prevent that.
    };
},
computed: {
    processing_submit_disabled:function () {
        const that = this;
        let disabled = false;
        // this is for initial and replacement
        if (this.placement_opt_selected != 'final') {
            if (this.new_product_bag_barcode == null || this.new_product_bag_barcode == '') {
                disabled = true;
            }
            _.each(that.product_barcode_fields, function(field) {
                if (field.value == null || field.value == '') {
                    disabled = true;
                }
            });

            // if there are any duplicate values in the product_barcode_fields, disable the submit button
            _.forEach(that.product_barcode_fields, function(field) {
                if (that.isDuplicate(field)) {
                    disabled = true;
                }
            });

            // if there are any values in the product_barcode_fields that are not 9 characters in length, disable the submit button
            _.forEach(that.product_barcode_fields, function(field) {
                if (that.isNotCorrectLength(field)) {
                    disabled = true;
                }
            });

            // if there are any values in the product_barcode_fields that do not start with the correct prefix, disable the submit button
            _.forEach(that.product_barcode_fields, function(field) {
                if (that.isNotCorrectPrefix(field)) {
                    disabled = true;
                }
            });
        }

        // if the new and used bag barcodes are the same, disable the submit button
        if (this.isNewAndUsedBagBarcodesSame()) {
            disabled = true;
        }

        // if the new bag barcode does not contain the household ID, disable the submit button
        if (this.doesNotContainHouseholdID('new')) {
            disabled = true;
        }

        // if the used bag barcode does not contain the household ID, disable the submit button
        if (this.doesNotContainHouseholdID('used')) {
            disabled = true;
        }

        // this is for replacement and final
        if (this.placement_opt_selected != 'initial') {
            if (this.used_product_bag_barcode == null || this.used_product_bag_barcode == '') {
                disabled = true;
            }
        }
        return disabled;
    },
    filteredOptions() {
      if (this.placement_status === 'prior' || this.placement_status === 'replacement') {
        return this.placement_options.filter(option => option.value !== 'initial');
      } else if (this.placement_status === 'initial') {
        return this.placement_options.filter(option => option.value === 'initial');
      } else {
        return this.placement_options;
      }
    },
},
created: function() {},
mounted() {
    this.$refs.searchInput.focus();
},
methods: {
    getProductEntrolledStructure: function () {
        const that = this;
        const fd = new FormData();
        fd.append("search_term", that.search_term);
        HTTP.post('srilanka/product_bag/get_structure/', fd).then((response) => {
            if(response.data.message != null) {
            this.ModalMessage = response.data.message;
            this.showModal = true;
            } else {
                this.cpes_id = response.data.cpes_id;
                this.household_id = response.data.household_id;
                this.cluster_number = response.data.cluster_number;
                this.products_required = response.data.products_required;
                this.product_barcode_fields = [];
                for (let i = 0; i < this.products_required; i++) {
                    this.product_barcode_fields.push(
                        { key: `product_barcode_${i + 1}`, value: '', text: `Barcode ${i + 1}` }
                    );
                }
                this.placement_status = response.data.placement_status;
                if (this.placement_status == 'initial') {
                    this.placement_opt_selected = 'initial';
                }
            }
        }).catch((error) => {
            HandleErrors.HandleErrors(error);
            this.ModalMessage = 'There was a problem. Please check household ID and try again.';
            this.showModal = true;
        });
    },
    submitProductBag: function() {
        const that = this;
        if (!that.submit_ready) {
            return;
        }
        // that.submit_ready = false;
        const fd = new FormData();
        fd.append("new_product_bag_barcode", that.new_product_bag_barcode);
        fd.append("used_product_bag_barcode", that.used_product_bag_barcode);
        fd.append("cpes_id", that.cpes_id);
        fd.append("products_required", that.products_required);
        fd.append("placement_type", that.placement_opt_selected);
        _.each(this.product_barcode_fields, function(field) {
            fd.append(field.key, field.value);
        });
        // first hit the check_product_bag_barcodes endpoint to check for duplicates
        HTTP.post('srilanka/product_bag/check_product_bag_barcodes/', fd).then((response) => {
            if (response.data.message != null) {
                this.ModalMessage = response.data.message;
                this.showModal = true;
            } else {
                // if no duplicates, submit the form
                HTTP.post('srilanka/product_bag/submit_new/', fd).then((response) => {
                    that.household_id = response.data.household_id;
                    _.each(that.form_fields, function(field) {
                        field.value = response.data[field.key];
                    });
                    if(response.data.message != null) {
                    this.ModalMessage = response.data.message;
                    this.showModal = true;
                    } else {
                        this.ModalMessage = 'Submitted successfully';
                        this.showModal = true;
                    }
                    that.submit_ready = true;
                }).catch((error) => {
                    HandleErrors.HandleErrors(error);
                    this.ModalMessage = 'There was a problem. Please check input values and try again.';
                    this.showModal = true;
                });
            }
        }).catch((error) => {
            HandleErrors.HandleErrors(error);
            this.ModalMessage = 'There was a problem. Please check input values and try again.';
            this.showModal = true;
        });

    },
    clearFields() {
        this.new_product_bag_barcode = '';
        this.used_product_bag_barcode = '';
        this.search_term = '';
        this.household_id = '';
        this.cluster_number = '';
        this.products_required = 0;
        this.product_barcode_fields = [];
        this.placement_opt_selected = 'replacement';
        this.placement_status = '';
    },
    isDuplicate(field) {
      return this.product_barcode_fields.some(f => {
        return f.key !== field.key && f.value === field.value && field.value !== '';
      });
    },
    isNotCorrectLength(field) {
      return field.value.length !== 9 && field.value !== '';  
    },
    isNotCorrectPrefix(field) {
        return field.value.substring(0, 2) !== this.cluster_barcode_map[this.cluster_number] && field.value !== '';
    },
    isNewAndUsedBagBarcodesSame() {
        return this.new_product_bag_barcode === this.used_product_bag_barcode && this.new_product_bag_barcode !== '';
    },
    doesNotContainHouseholdID(category) {
        if (category == 'new') {
            if (this.new_product_bag_barcode == null || this.new_product_bag_barcode == '') {
                return false;
            }
            return !this.new_product_bag_barcode.includes(this.household_id);
        } else if (category == 'used') {
            if (this.used_product_bag_barcode == null || this.used_product_bag_barcode == '') {
                return false;
            }
            return !this.used_product_bag_barcode.includes(this.household_id);
        }
        return false;
    },
    advanceInput(index) {
        // Note that if we ever upgrade this to Vue 3, this will break:
        // https://stackoverflow.com/questions/52086128/vue-js-ref-inside-the-v-for-loop
        if (index == 0) {
            let element = document.getElementById('PBInput1');
            console.log(element)
            if (element) {
                element.focus();
            }
        } else if (index == 999) {
        // I'VE FOUND IT'S TRIVIALLY EASY TO ACCIDENTALLY SUBMIT THE FORM BY SCANNING AGAIN
        // SO I'M COMMENTING THIS OUT FOR NOW
        //     this.$refs.SubmitButton.focus();
            console.log('submittting');
            
        } else {
            let nextInput = index + 1;
            let element = document.getElementById('PBInput' + nextInput);
            console.log(element)
            if (element) {
                element.focus();
            } else {
                this.$refs.UPBBInput.focus();
            }
        }
    },
}
};
</script>

<style scoped></style>